import { Amplify, Auth } from 'aws-amplify'
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  CookieStorage,
} from 'amazon-cognito-identity-js'

export const cookieDomain = process.env.NEXT_PUBLIC_DOMAIN as string
export const userPoolId = process.env.NEXT_PUBLIC_USER_POOL_ID as string
export const clientId = process.env.NEXT_PUBLIC_CLIENT_ID as string
export const cognitoDomain = process.env.NEXT_PUBLIC_COGNITO_DOMAIN as string
export const linkedInProviderName =
  (process.env.NEXT_PUBLIC_COGNITO_LINKEDIN_PROVIDER_NAME as string) ??
  'LinkedIn'
export const cognitoRegion =
  (process.env.NEXT_PUBLIC_AWS_REGION as string) ?? userPoolId?.split('_')[0]

const getBrowserOrigin = () => {
  if (typeof window === 'undefined') return ''
  return window.location.origin
}

const getRedirectSignIn = () =>
  (process.env.NEXT_PUBLIC_COGNITO_REDIRECT_SIGN_IN as string) ??
  `${getBrowserOrigin()}/oauth/callback`

const getRedirectSignOut = () =>
  (process.env.NEXT_PUBLIC_COGNITO_REDIRECT_SIGN_OUT as string) ??
  `${getBrowserOrigin()}/login`

let configured = false
const isLocalCookieDomain = cookieDomain === 'localhost'

export type EmbedHostedAuthSession = {
  accessToken: string
  idToken: string
  refreshToken?: string
  username: string
}

export const cognitoCookieStorageOptions = {
  domain: cookieDomain,
  path: '/',
  expires: 365,
  sameSite: isLocalCookieDomain ? ('lax' as const) : ('none' as const),
  secure: !isLocalCookieDomain,
}

export const userPool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId,
  Storage: new CookieStorage(cognitoCookieStorageOptions),
})

let embeddedCognitoUser: CognitoUser | null = null

export const getCurrentCognitoUser = () =>
  embeddedCognitoUser ?? userPool.getCurrentUser()

export const clearEmbeddedCognitoSession = () => {
  embeddedCognitoUser = null
}

export const hydrateEmbeddedCognitoSession = ({
  accessToken,
  idToken,
  refreshToken,
  username,
}: EmbedHostedAuthSession) => {
  const session = new CognitoUserSession({
    AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
    IdToken: new CognitoIdToken({ IdToken: idToken }),
    ...(refreshToken
      ? { RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshToken }) }
      : {}),
  })
  const cognitoUser = new CognitoUser({
    Pool: userPool,
    Storage: new CookieStorage(cognitoCookieStorageOptions),
    Username: username,
  })

  cognitoUser.setSignInUserSession(session)
  embeddedCognitoUser = cognitoUser

  return session
}

export const getEmbedHostedAuthSession = (
  cognitoUser: CognitoUser
): EmbedHostedAuthSession | undefined => {
  const session = cognitoUser.getSignInUserSession()
  if (!session) return undefined

  const refreshToken = session.getRefreshToken()?.getToken?.()

  return {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    ...(refreshToken ? { refreshToken } : {}),
    username: cognitoUser.getUsername(),
  }
}

export const configureCognito = () => {
  if (configured || !userPoolId || !clientId) return

  Amplify.configure({
    Auth: {
      region: cognitoRegion,
      userPoolId,
      userPoolWebClientId: clientId,
      cookieStorage: cognitoCookieStorageOptions,
      oauth: cognitoDomain
        ? {
            domain: cognitoDomain,
            scope: [
              'openid',
              'email',
              'profile',
              'aws.cognito.signin.user.admin',
            ],
            redirectSignIn: getRedirectSignIn(),
            redirectSignOut: getRedirectSignOut(),
            responseType: 'code',
          }
        : undefined,
    },
    ssr: true,
  })

  configured = true
}

export const signInWithGoogle = async () => {
  configureCognito()

  if (!cognitoDomain) {
    throw new Error('NEXT_PUBLIC_COGNITO_DOMAIN is required for Google SSO')
  }

  await Auth.federatedSignIn({
    provider: CognitoHostedUIIdentityProvider.Google,
  })
}

export const signInWithLinkedIn = async () => {
  configureCognito()

  if (!cognitoDomain) {
    throw new Error('NEXT_PUBLIC_COGNITO_DOMAIN is required for LinkedIn SSO')
  }

  await Auth.federatedSignIn({
    customProvider: linkedInProviderName,
  })
}

export const getHostedAuthenticatedUser = async () => {
  configureCognito()
  return Auth.currentAuthenticatedUser()
}

export const signOutOfCognito = async () => {
  configureCognito()
  await Auth.signOut()
}
